import React, { useState, useEffect } from 'react';
import '../CSS/JobUpload.css';
import '../CSS/DirectorySearch.css';

interface JobDetail {
  jobId: number;
  uniAffil: string;
  jobTitle: string;
  isAvailable: boolean;
}

interface PlacementNews {
  placementId: number;
  placementTitle: string;
}

interface MonthlyNews {
  monthlyNewsId: number;
  monthlyNewsTitle: string;
}

interface SubscriberInfo {
  email: string;
  firstName: string;
  lastName: string;
  createdDate?: string;
}

const DirectorySearch: React.FC = () => {
  const [selectedDatabase, setSelectedDatabase] = useState<string>('');
  const [searchQuery, setSearchQuery] = useState('');
  const [jobDetails, setJobDetails] = useState<JobDetail[]>([]);
  const [placementNews, setPlacementNews] = useState<PlacementNews[]>([]);
  const [monthlyNews, setMonthlyNews] = useState<MonthlyNews[]>([]);
  const [subscriberInfo, setSubscriberInfo] = useState<SubscriberInfo[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [dateFrom, setDateFrom] = useState<string>('');
  const [dateTo, setDateTo] = useState<string>('');

  // Fetch data when database is selected
  useEffect(() => {
    if (!selectedDatabase || selectedDatabase === '') {
      setJobDetails([]);
      setPlacementNews([]);
      setMonthlyNews([]);
      setSubscriberInfo([]);
      setSearchQuery('');
      setDateFrom('');
      setDateTo('');
      return;
    }

    const fetchData = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const token = localStorage.getItem('adminToken') || sessionStorage.getItem('adminToken');
        const headers: HeadersInit = token ? {
          'Authorization': `Bearer ${token}`
        } : {};

        if (selectedDatabase === 'Job Details') {
          const response = await fetch('http://127.0.0.1:3060/api/admin/create-job/all', {
            method: 'GET',
            headers
          });
          const data = await response.json();
          
          if (data.success && data.data?.jobs) {
            setJobDetails(data.data.jobs);
          } else {
            setJobDetails([]);
            setError('No job details found');
          }
        } else if (selectedDatabase === 'Placement News') {
          const response = await fetch('http://127.0.0.1:3060/api/placements', {
            method: 'GET',
            headers
          });
          const data = await response.json();
          
          if (data.success && data.data) {
            setPlacementNews(data.data);
          } else {
            setPlacementNews([]);
            setError('No placement news found');
          }
        } else if (selectedDatabase === 'Monthly News') {
          const response = await fetch('http://127.0.0.1:3060/api/admin/monthly-news', {
            method: 'GET',
            headers
          });
          const data = await response.json();
          
          if (data.success && data.data) {
            setMonthlyNews(data.data);
          } else {
            setMonthlyNews([]);
            setError('No monthly news found');
          }
        } else if (selectedDatabase === 'Subscriber Info') {
          const response = await fetch('http://127.0.0.1:3060/api/admin/subscriber-information', {
            method: 'GET',
            headers
          });
          const data = await response.json();
          
          if (data.success && data.data) {
            setSubscriberInfo(data.data);
          } else {
            setSubscriberInfo([]);
            setError('No subscriber info found');
          }
        }
      } catch (error: any) {
        console.error('Error fetching data:', error);
        setError('Error fetching data. Please try again.');
        setJobDetails([]);
        setPlacementNews([]);
        setMonthlyNews([]);
        setSubscriberInfo([]);
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [selectedDatabase]);

  // Filter data based on search query
  const filteredJobDetails = searchQuery.trim() === ''
    ? jobDetails
    : jobDetails.filter(job => 
        job.jobId.toString().includes(searchQuery) ||
        job.uniAffil.toLowerCase().includes(searchQuery.toLowerCase()) ||
        job.jobTitle.toLowerCase().includes(searchQuery.toLowerCase()) ||
        job.isAvailable.toString().toLowerCase().includes(searchQuery.toLowerCase())
      );

  const filteredPlacementNews = searchQuery.trim() === ''
    ? placementNews
    : placementNews.filter(placement => 
        placement.placementId.toString().includes(searchQuery) ||
        placement.placementTitle.toLowerCase().includes(searchQuery.toLowerCase())
      );

  const filteredMonthlyNews = searchQuery.trim() === ''
    ? monthlyNews
    : monthlyNews.filter(news => 
        news.monthlyNewsId.toString().includes(searchQuery) ||
        news.monthlyNewsTitle.toLowerCase().includes(searchQuery.toLowerCase())
      );

  // Filter subscriber info by search query and date range
  const filteredSubscriberInfo = subscriberInfo.filter(subscriber => {
    // Text search filter
    const matchesSearch = searchQuery.trim() === '' || 
      subscriber.firstName.toLowerCase().includes(searchQuery.toLowerCase()) ||
      subscriber.lastName.toLowerCase().includes(searchQuery.toLowerCase()) ||
      subscriber.email.toLowerCase().includes(searchQuery.toLowerCase());

    // Date range filter
    let matchesDateRange = true;
    if (dateFrom || dateTo) {
      if (subscriber.createdDate) {
        const subscriberDate = new Date(subscriber.createdDate);
        subscriberDate.setHours(0, 0, 0, 0); // Reset time to start of day

        if (dateFrom) {
          const fromDate = new Date(dateFrom);
          fromDate.setHours(0, 0, 0, 0);
          if (subscriberDate < fromDate) {
            matchesDateRange = false;
          }
        }

        if (dateTo && matchesDateRange) {
          const toDate = new Date(dateTo);
          toDate.setHours(23, 59, 59, 999); // End of day
          if (subscriberDate > toDate) {
            matchesDateRange = false;
          }
        }
      } else {
        // If no createdDate and date filter is set, exclude it
        matchesDateRange = false;
      }
    }

    return matchesSearch && matchesDateRange;
  });

  const handleDatabaseChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setSelectedDatabase(e.target.value);
    setSearchQuery('');
    setDateFrom('');
    setDateTo('');
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value);
  };

  const handleDateFromChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setDateFrom(e.target.value);
  };

  const handleDateToChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setDateTo(e.target.value);
  };

  const formatDate = (dateString: string | undefined): string => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const convertToCSV = (data: any[], displayHeaders: string[], dataKeys: string[], filename: string) => {
    if (data.length === 0) {
      alert('No data to download');
      return;
    }

    // Create CSV header row with display names
    const csvHeaders = displayHeaders.join(',');
    
    // Create CSV data rows using dataKeys to access the actual data
    const csvRows = data.map(row => {
      return dataKeys.map(key => {
        let value = row[key] || '';
        // Convert boolean to Yes/No for isAvailable
        if (key === 'isAvailable') {
          value = value ? 'Yes' : 'No';
        }
        // Format dates
        if (key === 'createdDate' && value) {
          const date = new Date(value);
          value = date.toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'long',
            day: 'numeric'
          });
        }
        // Escape commas and quotes in values
        const stringValue = String(value);
        if (stringValue.includes(',') || stringValue.includes('"') || stringValue.includes('\n')) {
          return `"${stringValue.replace(/"/g, '""')}"`;
        }
        return stringValue;
      }).join(',');
    });

    // Combine headers and rows
    const csvContent = [csvHeaders, ...csvRows].join('\n');

    // Create blob and download
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', filename);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleDownloadCSV = () => {
    if (!selectedDatabase || selectedDatabase === '') {
      return;
    }

    let data: any[] = [];
    let displayHeaders: string[] = [];
    let dataKeys: string[] = [];
    let filename = '';

    if (selectedDatabase === 'Job Details') {
      data = filteredJobDetails;
      displayHeaders = ['Job ID', 'University', 'Job Title', 'Available'];
      dataKeys = ['jobId', 'uniAffil', 'jobTitle', 'isAvailable'];
      filename = 'job-details.csv';
    } else if (selectedDatabase === 'Placement News') {
      data = filteredPlacementNews;
      displayHeaders = ['Placement News ID', 'Placement News Title'];
      dataKeys = ['placementId', 'placementTitle'];
      filename = 'placement-news.csv';
    } else if (selectedDatabase === 'Monthly News') {
      data = filteredMonthlyNews;
      displayHeaders = ['Monthly News ID', 'Monthly News Title'];
      dataKeys = ['monthlyNewsId', 'monthlyNewsTitle'];
      filename = 'monthly-news.csv';
    } else if (selectedDatabase === 'Subscriber Info') {
      data = filteredSubscriberInfo;
      displayHeaders = ['First Name', 'Last Name', 'E-mail', 'Created Date'];
      dataKeys = ['firstName', 'lastName', 'email', 'createdDate'];
      filename = 'subscriber-info.csv';
    }

    if (data.length > 0) {
      convertToCSV(data, displayHeaders, dataKeys, filename);
    } else {
      alert('No data to download');
    }
  };

  return (
    <div className="job-upload-page">
      <div className="job-upload-container">
        <div className="directory-search-header">
          <h1 className="job-upload-title">Directory Search</h1>
          <button
            type="button"
            onClick={handleDownloadCSV}
            className="directory-download-csv-button"
            disabled={!selectedDatabase || selectedDatabase === ''}
          >
            Download CSV
          </button>
        </div>
        
        <div className="directory-search-controls">
          <div className="directory-search-field">
            <label htmlFor="databaseSelect" className="job-upload-label">
              Select a Database to Search
            </label>
            <select
              id="databaseSelect"
              value={selectedDatabase}
              onChange={handleDatabaseChange}
              className="directory-search-select"
            >
              <option value="">Select an Option</option>
              <option value="Job Details">Job Details</option>
              <option value="Placement News">Placement News</option>
              <option value="Monthly News">Monthly News</option>
              <option value="Subscriber Info">Subscriber Info</option>
            </select>
          </div>
          
          <div className="directory-search-field">
            <label htmlFor="searchInput" className="job-upload-label">
              Search
            </label>
            <input
              type="text"
              id="searchInput"
              value={searchQuery}
              onChange={handleSearchChange}
              className="directory-search-input"
              placeholder="Search..."
              disabled={!selectedDatabase || selectedDatabase === ''}
            />
          </div>
        </div>

        {/* Date Range Filter - Only for Subscriber Info */}
        {selectedDatabase === 'Subscriber Info' && (
          <div className="directory-search-date-filters">
            <div className="directory-search-field">
              <label htmlFor="dateFrom" className="job-upload-label">
                From Date
              </label>
              <input
                type="date"
                id="dateFrom"
                value={dateFrom}
                onChange={handleDateFromChange}
                className="directory-search-date-input"
              />
            </div>
            <div className="directory-search-field">
              <label htmlFor="dateTo" className="job-upload-label">
                To Date
              </label>
              <input
                type="date"
                id="dateTo"
                value={dateTo}
                onChange={handleDateToChange}
                className="directory-search-date-input"
              />
            </div>
          </div>
        )}

        {isLoading && (
          <div className="directory-search-loading">Loading data...</div>
        )}

        {error && !isLoading && (
          <div className="directory-search-error">{error}</div>
        )}

        {!isLoading && !error && selectedDatabase && (
          <div className="directory-search-table-container">
            {selectedDatabase === 'Job Details' && (
              <div className="directory-search-table directory-table-4-cols">
                <div className="directory-table-header">
                  <div className="directory-table-cell">Job ID</div>
                  <div className="directory-table-cell">University</div>
                  <div className="directory-table-cell">Job Title</div>
                  <div className="directory-table-cell">Available</div>
                </div>
                {filteredJobDetails.length === 0 ? (
                  <div className="directory-table-empty">No job details found</div>
                ) : (
                  filteredJobDetails.map((job) => (
                    <div key={job.jobId} className="directory-table-row">
                      <div className="directory-table-cell">{job.jobId}</div>
                      <div className="directory-table-cell">{job.uniAffil}</div>
                      <div className="directory-table-cell">{job.jobTitle}</div>
                      <div className="directory-table-cell">{job.isAvailable ? 'Yes' : 'No'}</div>
                    </div>
                  ))
                )}
              </div>
            )}

            {selectedDatabase === 'Placement News' && (
              <div className="directory-search-table directory-table-2-cols">
                <div className="directory-table-header">
                  <div className="directory-table-cell">Placement News ID</div>
                  <div className="directory-table-cell">Placement News Title</div>
                </div>
                {filteredPlacementNews.length === 0 ? (
                  <div className="directory-table-empty">No placement news found</div>
                ) : (
                  filteredPlacementNews.map((placement) => (
                    <div key={placement.placementId} className="directory-table-row">
                      <div className="directory-table-cell">{placement.placementId}</div>
                      <div className="directory-table-cell">{placement.placementTitle}</div>
                    </div>
                  ))
                )}
              </div>
            )}

            {selectedDatabase === 'Monthly News' && (
              <div className="directory-search-table directory-table-2-cols">
                <div className="directory-table-header">
                  <div className="directory-table-cell">Monthly News ID</div>
                  <div className="directory-table-cell">Monthly News Title</div>
                </div>
                {filteredMonthlyNews.length === 0 ? (
                  <div className="directory-table-empty">No monthly news found</div>
                ) : (
                  filteredMonthlyNews.map((news) => (
                    <div key={news.monthlyNewsId} className="directory-table-row">
                      <div className="directory-table-cell">{news.monthlyNewsId}</div>
                      <div className="directory-table-cell">{news.monthlyNewsTitle}</div>
                    </div>
                  ))
                )}
              </div>
            )}

            {selectedDatabase === 'Subscriber Info' && (
              <div className="directory-search-table directory-table-4-cols-subscriber">
                <div className="directory-table-header">
                  <div className="directory-table-cell">First Name</div>
                  <div className="directory-table-cell">Last Name</div>
                  <div className="directory-table-cell">E-mail</div>
                  <div className="directory-table-cell">Created Date</div>
                </div>
                {filteredSubscriberInfo.length === 0 ? (
                  <div className="directory-table-empty">No subscriber info found</div>
                ) : (
                  filteredSubscriberInfo.map((subscriber) => (
                    <div key={subscriber.email} className="directory-table-row">
                      <div className="directory-table-cell">{subscriber.firstName}</div>
                      <div className="directory-table-cell">{subscriber.lastName || ''}</div>
                      <div className="directory-table-cell">{subscriber.email}</div>
                      <div className="directory-table-cell">{formatDate(subscriber.createdDate)}</div>
                    </div>
                  ))
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default DirectorySearch;

